import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs, IconButton, InputAdornment, ListItemButton, TextField, Typography, LinearProgress } from '@mui/material'

import { Pocket } from '../utils/dexie/db'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import Loader from '../components/common/loader'
import HelpIcon from '../assets/icons/help-icon.svg'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import EditIcon from '../assets/icons/edit-icon.svg'
import HashIcon from '../assets/icons/hash-icon.svg'
import TicketIcon from '../assets/icons/ticket-icon.svg'
import Pagination from '../components/common/pagination'
import SearchIcon from '../assets/icons/search-icon.svg'
import { textFieldWhiteBg } from '../theme/custom-styles'
import { handleIsValidMatch } from '../utils/common.utils'
import AccountIcon from '../assets/icons/account-icon.svg'
import DownloadIcon from '../assets/icons/download-icon.svg'
import CalendarIcon from '../assets/icons/calendar-icon.svg'
import { getCustomerCompanyPockets } from '../api/customers.api'
import MoneyGreenIcon from '../assets/icons/money-icon-green.svg'
import { BalanceTransactionType, countriesCurrencies } from '../lib'
import { CustomerCompanyFunding, FormError, Payment } from '../types'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { getCustomerCompanyMoneyMovements } from '../api/app-queries.api'
import { mergeAndSortByConsecutiveNumber, numberWithCommas } from '../utils'
import { getCustomerCompanyMoneyMovementsBySearch } from '../api/app-data.api'
import { PocketsTransfersModal } from '../components/pockets-management/pockets-transfers-modal'
import { PaymentsHistoryModal } from '../components/insights-details-management/payments-history-modal'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { CustomerCompanyFundingDetailsModal } from '../components/customer-company-details-management/customer-company-funding-details-modal'

const CustomerCompanyMoneyMovementsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const currentListPageRef = useRef<number>(1)
  const selectedPocketIdRef = useRef<string>('')
  const { userSession } = useContext(UserContext)
  const showPocketsMovementsRef = useRef<boolean>(false)
  const movementsHistoryDataRef = useRef<Array<any>>([])
  const { setScrollToTop } = useContext(AppStateContext)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const movementsHistoryPocketDataRef = useRef<Array<any>>([])
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { isAuth, activateAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['customer-company-details-management', 'system-users-management'])

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [hasMultipleRows, setHasMultipleRows] = useState(false)
  const [isComponentMount, setIsComponentMount] = useState(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [loadingPocketsData, setLoadingPocketsData] = useState(true)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [loadingMovementsData, setLoadingMovementsData] = useState(true)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [moneyMovementToSearch, setMoneyMovementToSearch] = useState<string>('')
  const [currentPocketsData, setCurrentPocketsData] = useState<Array<Pocket>>([])
  const [movementsHistoryList, setMovementsHistoryList] = useState<Array<any>>([])
  const [movementsHistoryData, setMovementsHistoryData] = useState<Array<any>>([])
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [openPaymentsHistoryModal, setOpenPaymentsHistoryModal] = useState<boolean>(false)
  const [openPocketsTransfersModal, setOpenPocketsTransfersModal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [selectedPocketTransferDetails, setSelectedPocketTransferDetails] = useState<any>({})
  const [movementsHistoryPocketData, setMovementsHistoryPocketData] = useState<Array<any>>([])
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [searchedMovementsHistoryData, setSearchedMovementsHistoryData] = useState<Array<any>>([])
  const [customerCompanyPaymentsData, setCustomerCompanyPaymentsData] = useState<Array<Partial<Payment>>>([])
  const [openCustomerCompanyFundingDetailsModal, setOpenCustomerCompanyFundingDetailsModal] = useState(false)
  const [selectedCustomerCompanyFunding, setSelectedCustomerCompanyFunding] = useState<CustomerCompanyFunding>()

  useEffect(() => {
    if (userSession && userSession.belongsTo !== 'holdings' && userSession.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetComponentData()

    const checkRows = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const firstItem = container.firstElementChild
      if (!firstItem) return

      const firstItemTop = firstItem.getBoundingClientRect().top
      const multipleRows = Array.from(container.children).some((item) => item.getBoundingClientRect().top > firstItemTop)

      setHasMultipleRows(multipleRows)
    }

    const resizeObserver = new ResizeObserver(checkRows)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    setCurrentListPage(1)
    currentListPageRef.current = 1

    if (selectedPocketId) {
      selectedPocketIdRef.current = selectedPocketId
      handleFilterMoneyMovementsByPocket()
      showPocketsMovementsRef.current = true
      return
    }

    selectedPocketIdRef.current = ''
    showPocketsMovementsRef.current = false
    movementsHistoryPocketDataRef.current = []
    setMovementsHistoryPocketData([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPocketId])

  useEffect(() => {
    if (!loadingPocketsData && !loadingMovementsData) {
      setLoadingLocalData(false)
    } else {
      setLoadingLocalData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPocketsData, loadingMovementsData])

  useEffect(() => {
    if (!appDataSocket) return
    if (!isComponentMount) return

    appDataSocket.on(`addNewMoneyMovement:${customerCompanyId}`, async (dataFromSocket: any) => {
      if (String(dataFromSocket.currency) === String(currency)) {
        const {
          data: { accessToken /* refreshToken */ }
        } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
        // activateAuth?.({ token: refreshToken })
        handleGetMoneyMovementsByCustomerCompany(accessToken)
      }
    })

    appDataSocket.on(`updateMoneyMovement:${customerCompanyId}`, async (dataFromSocket: any) => {
      if (String(dataFromSocket.currency) === String(currency)) {
        const {
          data: { accessToken /* refreshToken */ }
        } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
        // activateAuth?.({ token: refreshToken })
        handleGetMoneyMovementsByCustomerCompany(accessToken)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized, isComponentMount])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage =
      moneyMovementToSearch === '' && searchedMovementsHistoryData.length === 0 && !showPocketsMovementsRef.current
        ? movementsHistoryData.slice(firstPageIndex, lastPageIndex)
        : moneyMovementToSearch === '' && showPocketsMovementsRef.current
          ? movementsHistoryPocketData.slice(firstPageIndex, lastPageIndex)
          : searchedMovementsHistoryData.slice(firstPageIndex, lastPageIndex)

    setMovementsHistoryList(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, movementsHistoryData, searchedMovementsHistoryData, movementsHistoryPocketData])

  const handleFilterMoneyMovementsByPocket = () => {
    const movementsHistoryDataByPocket = [...movementsHistoryDataRef.current].filter((movementHistoryData) => {
      if (movementHistoryData.fundingId && String(movementHistoryData.pocketDetails._id) === String(selectedPocketIdRef.current) && movementHistoryData.pocketDetails.main) {
        return movementHistoryData
      }

      if (
        movementHistoryData.paymentId &&
        movementHistoryData.joinedData &&
        Array.isArray(movementHistoryData.joinedData) &&
        movementHistoryData.joinedData.length > 0 &&
        String(movementHistoryData.joinedData[0].fundsFromPocket) === String(selectedPocketIdRef.current)
      ) {
        return movementHistoryData
      }

      if (movementHistoryData.movementId) {
        const movementDetails = Array.isArray(movementHistoryData.movementDetails) ? movementHistoryData.movementDetails[0] : movementHistoryData.movementDetails

        if (String(movementHistoryData.movementDetails._id) === String(selectedPocketIdRef.current) || String(movementDetails.transferData.toPocketId) === String(selectedPocketIdRef.current)) {
          return movementHistoryData
        }
      }

      return false
    })

    movementsHistoryPocketDataRef.current = movementsHistoryDataByPocket
    setMovementsHistoryPocketData(movementsHistoryDataByPocket)
  }

  const handleGetComponentData = async () => {
    let localCustomerCompanyPocketsToSet: any = []
    let localCustomerCompanyMoneyMovementsToSet: any = []
    const localCustomerCompanyPockets = globalThis.sessionStorage.getItem(`customerCompanyPockets:${customerCompanyId}:${currency}`)
    const localCustomerCompanyMoneyMovements = globalThis.sessionStorage.getItem(`customerCompanyMoneyMovements:${customerCompanyId}:${currency}`)

    if (localCustomerCompanyPockets && localCustomerCompanyMoneyMovements) {
      localCustomerCompanyPocketsToSet = JSON.parse(localCustomerCompanyPockets)
      localCustomerCompanyMoneyMovementsToSet = JSON.parse(localCustomerCompanyMoneyMovements)

      setMovementsHistoryData(localCustomerCompanyMoneyMovementsToSet)
      setCustomerCompanyPaymentsData(localCustomerCompanyMoneyMovementsToSet)
      movementsHistoryDataRef.current = localCustomerCompanyMoneyMovementsToSet
      handleFilterMoneyMovementsByPocket()
      setLoadingMovementsData(false)

      setCurrentPocketsData(localCustomerCompanyPocketsToSet)
      setLoadingPocketsData(false)

      if (isComponentMount === false) {
        setIsComponentMount(true)
      }
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      handleGetPocketsDataByCustomerCompany(accessToken)
      handleGetMoneyMovementsByCustomerCompany(accessToken)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetMoneyMovementsByCustomerCompany = async (accessToken: string) => {
    try {
      const { data } = await getCustomerCompanyMoneyMovements(String(customerCompanyId), currency, accessToken)

      setLoadingMovementsData(false)
      // setMovementsHistoryList(moneyMovements)
      setMovementsHistoryData(data.customerCompanyBalanceHistoryData)
      setCustomerCompanyPaymentsData(data.customerCompanyBalanceHistoryData)
      movementsHistoryDataRef.current = data.customerCompanyBalanceHistoryData
      handleFilterMoneyMovementsByPocket()
      globalThis.sessionStorage.setItem(`customerCompanyMoneyMovements:${customerCompanyId}:${currency}`, JSON.stringify(data.customerCompanyBalanceHistoryData))

      if (isComponentMount === false) {
        setIsComponentMount(true)
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      // if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
      //   handleCloseSession()
      // }
    }
  }

  const handleGetPocketsDataByCustomerCompany = async (accessToken: string) => {
    try {
      const {
        data: { customerCompanyPockets }
      } = await getCustomerCompanyPockets(String(customerCompanyId), currency, accessToken)

      setCurrentPocketsData(customerCompanyPockets)
      globalThis.sessionStorage.setItem(`customerCompanyPockets:${customerCompanyId}:${currency}`, JSON.stringify(customerCompanyPockets))
      setLoadingPocketsData(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleFormatDocumentNumber = (docNumber: string, docType: string): string => {
    if (!/^\d+$/.test(docNumber)) return docNumber

    if (docType === 'N') {
      const main = docNumber.slice(0, -1)
      const last = docNumber.slice(-1)
      const formattedMain = Number(main).toLocaleString('es-CO')
      return `${formattedMain}-${last}`
    }

    if (docType === 'C') {
      return Number(docNumber).toLocaleString('es-CO')
    }

    return docNumber
  }

  const handleSetMoneyMovementToSearch = (moneyMovementToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1

    setErrors({})
    setCurrentListPage(1)
    setMoneyMovementToSearch(moneyMovementToSearchData)

    if (moneyMovementToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentMovementsHistoryDataState = movementsHistoryDataRef.current ?? []
      const movementsHistoryPerPage = currentMovementsHistoryDataState.slice(firstPageIndex, lastPageIndex)

      setMovementsHistoryList(movementsHistoryPerPage)
      setSearchedMovementsHistoryData([])

      if (showError) setErrors({ moneyMovementToSearch: t('searchField.errors.emptyField', { ns: 'payments-to-review-management' }) })
      return
    }

    const currentFullMovementsHistoryData = selectedPocketIdRef.current === '' ? [...(movementsHistoryDataRef.current as Array<any>)] : [...(movementsHistoryPocketDataRef.current as Array<any>)]
    const filteredMovementsHistory = currentFullMovementsHistoryData.filter((movementHistoryData) => {
      const movementDate = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(format(new Date(movementHistoryData.createdAt), 'dd/MM/yyyy | HH:mm:ss')).toLowerCase())
      const movementBeneficiaryName = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(movementHistoryData.beneficiaryName).toLowerCase())
      const movementBeneficiaryIdNumber = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(movementHistoryData.beneficiaryIdNumber).toLowerCase(), 'document')
      const movementTypeReference = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(handleGetMovementType(movementHistoryData)).toLowerCase())
      const movementHistoryReference = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(handleGetMovementReference(movementHistoryData)).toLowerCase())
      const movementHistoryAmountAsText = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(handleGetMovementAmount(movementHistoryData)).toLowerCase(), 'currency')
      const movementHistoryAmountAsNumber = handleIsValidMatch(moneyMovementToSearchData.toLowerCase(), String(handleGetMovementAmount(movementHistoryData)).toLowerCase().replace(/\D/g, ''), 'currency')

      return movementDate || movementHistoryReference || movementTypeReference || movementHistoryAmountAsText || movementHistoryAmountAsNumber || movementBeneficiaryName || movementBeneficiaryIdNumber
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const movementsHistoryPerPage = filteredMovementsHistory.slice(firstPageIndex, lastPageIndex)

    setMovementsHistoryList(movementsHistoryPerPage)
    setSearchedMovementsHistoryData(filteredMovementsHistory)
  }

  const handleGetMovementType = (movementHistoryData: any) => {
    if (movementHistoryData.type === BalanceTransactionType.CREDITING_FUNDS) {
      return t('moneyMovements.types.creditingFunds', { ns: 'reports-management' }).toUpperCase()
    }

    if (movementHistoryData.type === BalanceTransactionType.CREDIT) {
      const pocketUseData = Array.isArray(movementHistoryData.joinedData)
        ? currentPocketsData.find((pocketData) => String(pocketData._id) === String(movementHistoryData.joinedData?.[0]?.fundsFromPocket))
        : currentPocketsData.find((pocketData) => String(pocketData._id) === String(movementHistoryData.joinedData?.fundsFromPocket))

      return `${t('moneyMovements.types.credit', { ns: 'reports-management' }).toUpperCase()} - ${pocketUseData?.name?.toUpperCase()}`
    }

    if (movementHistoryData.type === BalanceTransactionType.DEBIT) {
      const pocketUseData = Array.isArray(movementHistoryData.joinedData)
        ? currentPocketsData.find((pocketData) => String(pocketData._id) === String(movementHistoryData.joinedData?.[0]?.fundsFromPocket))
        : currentPocketsData.find((pocketData) => String(pocketData._id) === String(movementHistoryData.joinedData?.fundsFromPocket))

      return `${t('moneyMovements.types.debit', { ns: 'reports-management' }).toUpperCase()} - ${pocketUseData?.name?.toUpperCase()}`
    }

    if (movementHistoryData.type === BalanceTransactionType.TRANSFER) {
      return t('moneyMovements.types.transfer', { ns: 'reports-management' }).toUpperCase()
    }
  }

  const handleGetMovementAmount = (movementHistoryData: any) => {
    if (movementHistoryData.type === BalanceTransactionType.CREDITING_FUNDS) {
      const movementAmount = movementHistoryData.pocketData ? Math.abs(Math.round(Number(movementHistoryData.pocketData.balanceAfter) - Number(movementHistoryData.pocketData.balanceBefore))) : 0

      return `+$ ${numberWithCommas(movementAmount)} ${currency.toUpperCase()}`
    }

    if (movementHistoryData.type === BalanceTransactionType.TRANSFER) {
      return `+$ ${numberWithCommas(Math.round(Number(movementHistoryData.movementDetails.movementAmount)))} ${currency.toUpperCase()}`
    }

    if (movementHistoryData.type === BalanceTransactionType.DEBIT) {
      if (Array.isArray(movementHistoryData.joinedData)) {
        return `-$ ${numberWithCommas(Math.round(Number(movementHistoryData.joinedData[0]?.amount)))} ${currency.toUpperCase()}`
      } else {
        return `-$ ${numberWithCommas(Math.round(Number(movementHistoryData.joinedData?.amount)))} ${currency.toUpperCase()}`
      }
    }

    if (movementHistoryData.type === BalanceTransactionType.CREDIT) {
      const movementAmount = Math.abs(Math.round(Number(movementHistoryData.balanceAfter) - Number(movementHistoryData.balanceBefore)))

      return `+$ ${numberWithCommas(Math.round(Number(movementAmount)))} ${currency.toUpperCase()}`
    }
  }

  const handleGetMovementReference = (movementHistoryData: any) => {
    if (movementHistoryData.type === BalanceTransactionType.CREDITING_FUNDS) {
      if (movementHistoryData.joinedData[0]) {
        return movementHistoryData.joinedData[0].description
      }

      if (movementHistoryData.joinedData) {
        return movementHistoryData.joinedData.description
      }

      return '-'
    }

    if (movementHistoryData.type === BalanceTransactionType.TRANSFER) {
      return movementHistoryData.beneficiaryName.toUpperCase()
    }

    if (movementHistoryData.type === BalanceTransactionType.DEBIT || movementHistoryData.type === BalanceTransactionType.CREDIT) {
      if (movementHistoryData.joinedData[0]) {
        return movementHistoryData.joinedData[0].referenceNumber
      }

      if (movementHistoryData.joinedData) {
        return movementHistoryData.joinedData.referenceNumber
      }
    }
  }

  const handleOpenMovementDetailsModal = (movementHistoryData: any) => {
    if (movementHistoryData.fundingId) {
      setOpenCustomerCompanyFundingDetailsModal(true)

      if (Array.isArray(movementHistoryData.joinedData)) {
        setSelectedCustomerCompanyFunding(movementHistoryData.joinedData[0])
      } else {
        setSelectedCustomerCompanyFunding(movementHistoryData.joinedData)
      }

      return
    }

    if (movementHistoryData.paymentId) {
      setOpenPaymentDetailsModal(true)

      if (Array.isArray(movementHistoryData.joinedData)) {
        setSelectedPaymentDetails(movementHistoryData.joinedData[0])
      } else {
        setSelectedPaymentDetails(movementHistoryData.joinedData)
      }

      return
    }

    setOpenPocketsTransfersModal(true)
    setSelectedPocketTransferDetails(movementHistoryData.movementDetails)
  }

  const handleSearchCustomerMoneyMovementDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)
      const {
        data: { accessToken, refreshToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      activateAuth?.({ token: refreshToken })

      const response = await getCustomerCompanyMoneyMovementsBySearch({ moneyMovementToSearch, belongsTo: String(customerCompanyId) }, accessToken)
      // const {
      //   data: { downloadDocumentPreSignedURL }
      // } = await getCustomerCompanyMoneyMovementsBySearch({ moneyMovementToSearch, belongsTo: String(customerCompanyId) }, accessToken)
      // const response = await axios.get(downloadDocumentPreSignedURL)

      if (response.data.docs.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        return
      }

      const customerCompanyPaymentsCurrentData = [...customerCompanyPaymentsData]
      const customerCompanyPaymentsUpdatedData = mergeAndSortByConsecutiveNumber(response.data.docs, customerCompanyPaymentsCurrentData)

      setMovementsHistoryList(response.data.docs)
      setSearchedMovementsHistoryData(response.data.docs)
      setMovementsHistoryData(customerCompanyPaymentsUpdatedData)
      setCustomerCompanyPaymentsData(customerCompanyPaymentsUpdatedData)
      movementsHistoryDataRef.current = customerCompanyPaymentsUpdatedData
      handleFilterMoneyMovementsByPocket()
      setSearchingInDatabase(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      {loadingLocalData && <Loader sx={{ position: 'fixed' }} />}
      <div className="section-container">
        <Breadcrumbs
          separator={
            <img
              src={NextIcon}
              alt="next-icon"
            />
          }
          sx={{ width: '100%' }}
          aria-label="breadcrumb"
        >
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/customer-companies')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.firstLevel')}
            </Typography>
          </ListItemButton>
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.secondLevel')}
            </Typography>
          </ListItemButton>
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('breadcrumbs.movementsHistory')}
          </Typography>
        </Breadcrumbs>
        <div
          ref={containerRef}
          className={`pockets-submenu-container ${hasMultipleRows ? 'has-multiple-rows' : ''}`}
        >
          <div
            onClick={() => setSelectedPocketId('')}
            className={`button-item ${String(selectedPocketId) === '' ? 'button-item-selected' : ''}`}
          >
            {t('filters.all', { ns: 'system-users-management' })?.toUpperCase()}
          </div>
          {currentPocketsData.map((pocketData: Pocket, index: number) => {
            return (
              <div
                key={index}
                onClick={() => setSelectedPocketId(String(pocketData._id))}
                className={`button-item ${String(pocketData._id) === String(selectedPocketId) ? 'button-item-selected' : ''}`}
              >
                {pocketData.name?.toUpperCase()}
              </div>
            )
          })}
        </div>
        <div className="customer-company-fundings-history-container">
          <div className="actions-bar">
            <div className="left-content">
              {/* <div className="title">
              {(movementsHistoryList.length === 0 && !showPocketsMovementsRef.current) || (showPocketsMovementsRef.current && movementsHistoryPocketData.length === 0)
                ? t('actionsBar.thereIsNoMovementsHistory')
                : showPocketsMovementsRef.current && movementsHistoryPocketData.length > 0
                ? t('actionsBar.movementsHistory', { movementsHistoryCount: movementsHistoryPocketData.length })
                : t('actionsBar.movementsHistory', { movementsHistoryCount: movementsHistoryData.length })}
            </div> */}
              <div className="title">
                {(movementsHistoryList.length === 0 && !showPocketsMovementsRef.current) || (showPocketsMovementsRef.current && movementsHistoryPocketData.length === 0)
                  ? t('actionsBar.thereIsNoMoneyMovementsHistory')
                  : t('actionsBar.moneyMovementsHistory')}
              </div>
              {/* !loadingLocalData && movementsHistoryList.length > 0 && ( */}
              {!loadingLocalData && (
                <div
                  style={{ cursor: 'pointer' }}
                  className="filter-button-container"
                  onClick={() => setOpenPaymentsHistoryModal(true)}
                >
                  <img
                    src={DownloadIcon}
                    alt="download-icon"
                  />
                  <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
                </div>
              )}
            </div>
            <TextField
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('searchField.paymentPlaceholder')}
              value={moneyMovementToSearch}
              error={!!errors.moneyMovementToSearch}
              helperText={errors.moneyMovementToSearch}
              className="search-input"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleSetMoneyMovementToSearch(moneyMovementToSearch, true)
                }
              }}
              onChange={(event) => handleSetMoneyMovementToSearch(event.target.value, true)}
              onBlur={() => {
                if (moneyMovementToSearch === '') setErrors({})
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                    >
                      <img
                        src={SearchIcon}
                        alt="search-icon"
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
        </div>
        {!loadingLocalData && movementsHistoryData.length > 0 && (
          <>
            <div className="table-header-container table-header-batches">
              <div className="text-header table-header-width-16">{t('moneyMovements.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}</div>
              <div className="text-header table-header-width-20">{t('moneyMovements.tableHeaders.beneficiary', { ns: 'reports-management' }).toUpperCase()}</div>
              <div className="text-header table-header-width-16">{t('moneyMovements.tableHeaders.documentNumber', { ns: 'reports-management' }).toUpperCase()}</div>
              <div className="text-header table-header-width-16">{t('moneyMovements.tableHeaders.type', { ns: 'reports-management' }).toUpperCase()}</div>
              <div className="text-header table-header-width-16 num-of-payments-batches customer-company-display-none-lg">{t('moneyMovements.tableHeaders.amount', { ns: 'reports-management' }).toUpperCase()}</div>
              <div className="text-header table-header-width-16 status-batches">{t('moneyMovements.tableHeaders.reference', { ns: 'reports-management' }).toUpperCase()}</div>
            </div>
            <div className="documents-list-container">
              {movementsHistoryList.map((movementHistoryData: any, index: number) => {
                return (
                  <div
                    key={index}
                    className="customer-details-list-item-data-container"
                    onClick={() => handleOpenMovementDetailsModal(movementHistoryData)}
                  >
                    <div className="customer-company-table-column customer-company-table-width-16 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={CalendarIcon}
                          alt="calendar-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{format(new Date(movementHistoryData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-20 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={AccountIcon}
                          alt="account-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{movementHistoryData.beneficiaryName.toUpperCase()}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-16 customer-company-display-none-lg max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={HashIcon}
                          alt="hash-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">
                        {movementHistoryData.beneficiaryIdNumber ? handleFormatDocumentNumber(movementHistoryData.beneficiaryIdNumber, movementHistoryData.idType) : 'N/A'}
                      </div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-16 customer-company-display-none-sm max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={EditIcon}
                          alt="edit-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{handleGetMovementType(movementHistoryData)}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-16 customer-company-display-none-sm max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={MoneyGreenIcon}
                          alt="money-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{handleGetMovementAmount(movementHistoryData)}</div>
                    </div>
                    <div className="customer-company-table-column customer-company-table-width-16 customer-company-display-none-sm max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={TicketIcon}
                          alt="ticket-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{handleGetMovementReference(movementHistoryData)}</div>
                    </div>
                  </div>
                )
              })}
            </div>
            {moneyMovementToSearch !== '' && movementsHistoryList.length === 0 && (
              <div className="customer-no-data-found-container">
                {searchingInDatabase === false ? (
                  <>
                    {noRegistriesFoundAtDatabase === false ? (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          {t('searchingResultTexts.case1.secondPart')}{' '}
                          <strong
                            className="customer-no-data-found-no-registries-text-click"
                            onClick={() => handleSearchCustomerMoneyMovementDataByRegex()}
                          >
                            {t('searchingResultTexts.case1.thirdPart')}
                          </strong>{' '}
                          {t('searchingResultTexts.case1.fourthPart')}
                        </div>
                        {errors.generalError && (
                          <div
                            style={{ marginTop: '0px' }}
                            className="error-message"
                          >
                            {errors.generalError}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          <img
                            src={HelpIcon}
                            alt="help-icon"
                          />
                        </div>
                        <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2')}</div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="customer-no-data-found-searching-progress-bar">
                      <LinearProgress />
                    </div>
                    <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3')}</div>
                  </>
                )}
              </div>
            )}
            <Pagination
              pageSize={pageSize}
              currentPage={currentListPage}
              handlePaginationPageChange={(page: number) => {
                setTimeout(() => {
                  setScrollToTop(true)
                  setCurrentListPage(page)
                  currentListPageRef.current = page
                }, 100)
              }}
              handleChangePageSize={(pageSizeToSet: string) => {
                setScrollToTop(true)
                setPageSize(Number(pageSizeToSet))
              }}
              totalCount={
                moneyMovementToSearch === '' && searchedMovementsHistoryData.length === 0 && !showPocketsMovementsRef.current
                  ? movementsHistoryData.length
                  : moneyMovementToSearch === '' && showPocketsMovementsRef.current
                    ? movementsHistoryPocketData.length
                    : searchedMovementsHistoryData.length
              }
            />
          </>
        )}
        {openPocketsTransfersModal && (
          <PocketsTransfersModal
            selectedPocketTransferDetails={selectedPocketTransferDetails}
            handleSetModalOpenState={(openState: boolean) => setOpenPocketsTransfersModal(openState)}
          />
        )}
        {openCustomerCompanyFundingDetailsModal && (
          <CustomerCompanyFundingDetailsModal
            selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
            handleSetModalOpenState={(openState: boolean) => setOpenCustomerCompanyFundingDetailsModal(openState)}
          />
        )}
        {openPaymentDetailsModal && (
          <Dialog
            errors={errors}
            hideActionsButtons
            handleCloseOptionAction={() => {
              setOpenPaymentDetailsModal(false)
              setSelectedPaymentDetails({})
            }}
            handleDialogOpenState={(openState: boolean) => {
              setOpenPaymentDetailsModal(openState)
              setSelectedPaymentDetails({})
            }}
            dialogTitle={t('paymentDetailsModal.titleDetails', { ns: 'customer-company-details-management' })}
          >
            <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
          </Dialog>
        )}
        {openPaymentsHistoryModal && (
          <PaymentsHistoryModal
            handleSetModalOpenState={(openState: boolean) => {
              setOpenPaymentsHistoryModal(openState)
            }}
            filtersFromOutside={{
              belongsTo: String(customerCompanyId),
              selectedPocketId: selectedPocketIdRef.current
            }}
            customerCompanyPaymentsData={customerCompanyPaymentsData}
          />
        )}
      </div>
    </>
  )
}

export default CustomerCompanyMoneyMovementsManagement
